// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { getTrend } from '../api/workbenchApi';
import RefreshSection from '../components/RefreshSection';

const fmtUSD = (v) => {
  const n = Number(v || 0);
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(0)}k`;
  return `$${n.toFixed(0)}`;
};
const STACK_COLOURS = [
  '#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6',
  '#06b6d4', '#ec4899', '#14b8a6', '#f97316', '#a855f7',
];

/**
 * Two charts:
 *   1. Combo: Revenue + COGS columns (left axis) + Gross / Net margin lines (right axis).
 *   2. Stacked bar: OpEx composition by category, period-over-period.
 */
const TrendDiagnostics = ({ mappingSessionId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    if (!mappingSessionId) return () => {};
    let cancelled = false;
    setLoading(true);
    setError(null);
    getTrend(mappingSessionId)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [mappingSessionId]);

  useEffect(() => load(), [load]);

  const comboSeries = useMemo(() => {
    if (!data) return [];
    return data.series.map((s) => ({
      period: s.period,
      revenue: Number(s.revenue || 0),
      cogs: Number(s.cogs || 0),
      gross_margin_pct: s.gross_margin_pct == null ? null : Number(s.gross_margin_pct) * 100,
      operating_margin_pct: s.operating_margin_pct == null ? null : Number(s.operating_margin_pct) * 100,
    }));
  }, [data]);

  const stackSeries = useMemo(() => {
    if (!data) return { rows: [], keys: [] };
    const periods = data.periods.map((p) => p.label);
    const keys = (data.opex_stack || []).map((s) => s.label);
    const rows = periods.map((p) => {
      const row = { period: p };
      (data.opex_stack || []).forEach((s) => {
        row[s.label] = Number(s.values[p] || 0);
      });
      return row;
    });
    return { rows, keys };
  }, [data]);

  if (loading && !data) {
    return (
      <div>
        <div className="qoe-wb-section-header">
          <h2>
            Trend &amp; Margin Diagnostics
            <RefreshSection onRefresh={load} busy={loading} label="Refresh trend diagnostics" />
          </h2>
        </div>
        <div className="qoe-wb-loading">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div>
        <div className="qoe-wb-section-header">
          <h2>
            Trend &amp; Margin Diagnostics
            <RefreshSection onRefresh={load} busy={loading} label="Refresh trend diagnostics" />
          </h2>
        </div>
        <div className="qoe-wb-row-error fail" style={{ maxWidth: 'unset' }}>
          {error}
        </div>
      </div>
    );
  }
  if (!data || comboSeries.length === 0) return null;

  return (
    <div>
      <div className="qoe-wb-section-header">
        <h2>Trend &amp; Margin Diagnostics</h2>
        <span className="qoe-wb-section-meta">{comboSeries.length} periods</span>
      </div>

      <div className="qoe-wb-trend-grid">
        <div className="qoe-wb-trend-card">
          <h3>Revenue, COGS &amp; Margins</h3>
          <div style={{ width: '100%', height: 320 }}>
            <ResponsiveContainer>
              <ComposedChart data={comboSeries} margin={{ top: 12, right: 16, left: 8, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                <XAxis
                  dataKey="period"
                  tick={{ fontSize: 10, fill: 'var(--text)' }}
                  interval={0}
                  angle={-30}
                  textAnchor="end"
                  height={60}
                />
                <YAxis
                  yAxisId="left"
                  tick={{ fontSize: 10, fill: 'var(--text)' }}
                  tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tick={{ fontSize: 10, fill: 'var(--text)' }}
                  tickFormatter={(v) => `${v.toFixed(0)}%`}
                  domain={[0, 100]}
                />
                <Tooltip
                  formatter={(value, key) =>
                    key === 'gross_margin_pct' || key === 'operating_margin_pct'
                      ? `${value?.toFixed(1)}%`
                      : fmtUSD(value)
                  }
                  contentStyle={{
                    background: 'var(--panel)',
                    border: '1px solid var(--border)',
                    borderRadius: 4,
                    fontSize: 12,
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar yAxisId="left" dataKey="revenue" fill="#3b82f6" name="Revenue" />
                <Bar yAxisId="left" dataKey="cogs" fill="#f59e0b" name="COGS" />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="gross_margin_pct"
                  stroke="#22c55e"
                  strokeWidth={2}
                  name="Gross Margin %"
                  dot={false}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="operating_margin_pct"
                  stroke="#8b5cf6"
                  strokeWidth={2}
                  name="Operating Margin %"
                  dot={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        {stackSeries.keys.length > 0 && (
          <div className="qoe-wb-trend-card">
            <h3>OpEx Composition</h3>
            <div style={{ width: '100%', height: 320 }}>
              <ResponsiveContainer>
                <ComposedChart data={stackSeries.rows} margin={{ top: 12, right: 16, left: 8, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                  <XAxis
                    dataKey="period"
                    tick={{ fontSize: 10, fill: 'var(--text)' }}
                    interval={0}
                    angle={-30}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: 'var(--text)' }}
                    tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
                  />
                  <Tooltip
                    formatter={(v) => fmtUSD(v)}
                    contentStyle={{
                      background: 'var(--panel)',
                      border: '1px solid var(--border)',
                      borderRadius: 4,
                      fontSize: 12,
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                  {stackSeries.keys.map((k, i) => (
                    <Bar
                      key={k}
                      dataKey={k}
                      stackId="opex"
                      fill={STACK_COLOURS[i % STACK_COLOURS.length]}
                      name={k}
                    />
                  ))}
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrendDiagnostics;
